import { ATTRIBUTION_KEY } from "@/constants/local-storage-keys";

import { MARKETING_PARAMS } from "./url-allowlist";

export type Touch = Record<string, string> & { landing_path: string; timestamp: string };
export interface AttributionState {
  first_touch: Touch;
  last_touch: Touch;
}

// `_gl` is GA's cross-domain linker, not a campaign signal.
const TOUCH_PARAMS = MARKETING_PARAMS.filter((key) => key !== "_gl");

export function extractTouch(href: string, referrer: string, nowMs: number): Touch | null {
  const url = new URL(href);
  const params: Record<string, string> = {};
  for (const key of TOUCH_PARAMS) {
    const value = url.searchParams.get(key);
    if (value) params[key] = value;
  }
  if (Object.keys(params).length === 0) return null;

  const touch: Touch = {
    ...params,
    landing_path: url.pathname,
    timestamp: new Date(nowMs).toISOString(),
  };
  // Foreign referrers keep origin only: their path may carry a third party's own
  // bearer/invite/reset token (depends on that site's Referrer-Policy), and their
  // query is not ours to allowlist regardless.
  try {
    const ref = new URL(referrer);
    if (ref.origin !== url.origin) touch.referrer = ref.origin;
  } catch {
    // empty or malformed referrer
  }
  return touch;
}

export function readAttribution(): AttributionState | null {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AttributionState> | null;
    return parsed?.first_touch && parsed.last_touch ? (parsed as AttributionState) : null;
  } catch {
    return null;
  }
}

// Returns true when this landing carried marketing params and was recorded.
export function recordAttribution(href: string, referrer: string, nowMs: number): boolean {
  const touch = extractTouch(href, referrer, nowMs);
  if (!touch) return false;
  const existing = readAttribution();
  const state: AttributionState = {
    first_touch: existing?.first_touch ?? touch,
    last_touch: touch,
  };
  try {
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(state));
  } catch {
    return false;
  }
  return true;
}

// Pre-attribution trait names; dashboards segment on these, so keep them populated.
const LEGACY_TRAIT_KEYS = ["utm_source", "utm_medium", "utm_campaign"] as const;

export function attributionTraits(state: AttributionState | null): Record<string, string> {
  if (!state) return {};
  const traits: Record<string, string> = {};
  for (const [prefix, touch] of [
    ["first_touch", state.first_touch],
    ["last_touch", state.last_touch],
  ] as const) {
    for (const [key, value] of Object.entries(touch)) traits[`${prefix}_${key}`] = value;
  }
  for (const key of LEGACY_TRAIT_KEYS) {
    const value = state.last_touch[key];
    if (value) traits[key] = value;
  }
  return traits;
}
